// Single source of truth for how the optional-skill bonus is worded across the UI.
//
// WHY THIS FILE EXISTS: the bonus used to be string-concatenated onto the END of the Evidence
// Coverage line in four separate places ("100% – High Confidence · Bonus +3.2"). It reads as though
// high coverage/confidence EARNED the bonus. It does not — the two are unrelated:
//   * evidence_coverage / confidence_label = how much of the required scorecard had gradeable
//     evidence (SCORING-CONTRACT §7 Step 3).
//   * optional_bonus                       = credit from skills the manager marked OPTIONAL,
//     i.e. expected_score = 0 (SCORING-CONTRACT §7 Step 5b).
// Four copies also meant four chances to drift. Import from here instead.

/** Label for the bonus row. Deliberately names the SOURCE (optional skills), not the effect. */
export const BONUS_LABEL = 'Optional-skill bonus:';

/** Lowercase inline form, for the print report's single-line meta strip. */
export const BONUS_LABEL_INLINE = 'optional-skill bonus';

/** The (i) tooltip: what it is, how it is earned, how to read it. One line each, kept short so the
 *  bubble stays scannable. */
export const BONUS_TIP_LINES: readonly string[] = [
  // Which skills — the wording follows Getting Started ("set the bar to 0 to make a skill optional"),
  // and the marker is the only on-screen cue: a 0 bar lights no slot on the row's marker track.
  'Optional = the skill’s bar is set to 0. In Performance Breakdown those rows show no marker beneath the bar.',
  'They can never cost points; if one is scored anyway it adds credit here, capped at +5.',
  'Pure upside: it lifts the final score, never drags it. “None” = every skill here has a minimum.',
  // Not links: .sc-note-tip is pointer-events:none, so anything clickable inside would be dead.
  'Change which skills are optional on the Scorecards page — Getting Started › Scorecards explains it.',
];

/** Build the small inline (i) that reveals BONUS_TIP_LINES on hover/focus. Shared by the snapshot
 *  panel (static markup mirrors this) and the cross-skill panel, so both explain it identically. */
export function bonusInfoIcon(doc: Document = document): HTMLElement {
  const wrap = doc.createElement('span');
  wrap.className = 'meta-info';
  wrap.tabIndex = 0;
  wrap.setAttribute('role', 'note');
  wrap.setAttribute('aria-label', 'What the optional-skill bonus means');
  const ico = doc.createElement('span');
  ico.className = 'meta-info-ico';
  ico.setAttribute('aria-hidden', 'true');
  ico.textContent = 'i';
  const tip = doc.createElement('span');
  tip.className = 'sc-note-tip';
  tip.setAttribute('role', 'tooltip');
  BONUS_TIP_LINES.forEach((line, i) => {
    if (i) tip.appendChild(doc.createElement('br'));
    tip.appendChild(doc.createTextNode(line));
  });
  wrap.append(ico, tip);
  return wrap;
}

/** Formatted value, e.g. "+3.2" — or "None" when the scorecard has no optional skills.
 *
 *  The row used to be hidden entirely at zero (and before that, appended to the coverage line only
 *  when non-zero). That made a legitimately-zero bonus indistinguishable from a broken one: on a
 *  scorecard where every skill carries an expected minimum there is simply nothing to credit, but the
 *  missing line read as the feature having regressed. Saying "None" states the fact instead. */
export function formatBonus(bonus: number): string {
  return bonus > 0 ? `+${bonus}` : 'None';
}

/** Coverage + confidence only — the bonus is rendered separately by every caller. */
export function coverageText(coveragePct: number, confidenceLabel: string): string {
  return `${Math.round(coveragePct * 100)}% – ${confidenceLabel} Confidence`;
}
